import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Button,
  CircularProgress,
  Box,
} from '@mui/material';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import {
  BENCH_CANDIDATE_SOURCE_LABELS,
  BENCH_CANDIDATE_SOURCE_COLORS,
  BENCH_CANDIDATE_STATUS_LABELS,
  BENCH_CANDIDATE_STATUS_COLORS,
  BENCH_CANDIDATE_MATCH_STATUS_LABELS,
  BENCH_CANDIDATE_MATCH_STATUS_COLORS,
  BENCH_CANDIDATE_MATCH_STATUSES,
  BENCH_CANDIDATE_STATUSES,
  BENCH_CANDIDATE_ELIGIBILITY_LABELS,
  BENCH_CANDIDATE_RUN_STATUSES,
  benchCandidateSourceSystemLabel,
} from '../../../constants/benchCandidates';
import { CandidateRow, TableWrapper, Primary, Secondary, ReasonList, PersonCell, PersonInfo } from './styles';
import { getChipSx } from './chipStyles';
import { formatDateOnly as formatDate } from './dates';

const daysLabel = (days) => {
  if (days === null || days === undefined) {
    return '—';
  }
  if (days === 0) {
    return 'today';
  }
  if (days < 0) {
    return `${Math.abs(days)}d ago`;
  }
  return `in ${days}d`;
};

const sourceSystemLabel = benchCandidateSourceSystemLabel;

// Feed vocabulary for a contract in force (TimeTrack says "Signed", not "Active").
const ACTIVE_CONTRACT_STATUSES = ['signed', 'active', 'upcoming'];

/**
 * Employment line for the Individual cell, from the feed's consultant (employment)
 * contract — distinct from the SOW. Only what the feed actually sent:
 *  - contract present: "contract 4918 · Signed · no end date specified", "contract 4918 ·
 *    Signed · ends 30/11/2026", "contract 4918 · Cancelled · terminated 27/03/2026"
 *    (the id is the feed's consultantContractId, omitted when not sent);
 *  - contract absent: "no contract data in the Americas feed" (prefixed with
 *    "inactive in Americas" when the feed's isActive flag is false).
 * Nothing is inferred from benchSource here — the Reason column owns that.
 * `left` (the server's LEFT_ORGANISATION rule — single source of truth) only
 * drives the tone.
 */
const employmentLineFor = (candidate) => {
  const left = (candidate.eligibility?.reasons || []).some((r) => r.code === 'LEFT_ORGANISATION');
  const system = sourceSystemLabel(candidate.sourceSystem);
  const status = candidate.consultantContractStatus;
  const endDate = candidate.consultantTerminationDate ? formatDate(candidate.consultantTerminationDate) : null;

  if (!status) {
    const parts = [];
    if (candidate.isActive === false) {
      parts.push(`inactive in ${system}`);
    }
    parts.push(`no contract data in the ${system} feed`);
    return { left, text: parts.join(' · ') };
  }
  const isActiveStatus = ACTIVE_CONTRACT_STATUSES.includes(String(status).toLowerCase());
  // No end date in the feed is just that — absence of data, not an open-ended contract.
  const tail = endDate
    ? ` · ${isActiveStatus ? 'ends' : 'terminated'} ${endDate}`
    : (isActiveStatus ? ' · no end date specified' : '');
  const id = candidate.consultantContractSourceId ? `contract ${candidate.consultantContractSourceId} · ${status}` : `contract ${status}`;
  return { left, text: `${id}${tail}` };
};

/** One readable line per eligibility reason, with the rule's detail where it helps. */
const reasonLine = (r, candidate) => {
  const label = BENCH_CANDIDATE_ELIGIBILITY_LABELS[r.code] || r.code;
  const d = r.detail || {};
  const feed = `${sourceSystemLabel(candidate?.sourceSystem)} feed`;
  if (r.code === 'REMOVED_UPSTREAM') {
    return `No longer listed in the ${feed}`;
  }
  if (r.code === 'UNKNOWN_BENCH_SOURCE') {
    return `Unmapped reason in the ${feed}`;
  }
  if (r.code === 'LEFT_ORGANISATION') {
    if (d.consultantContractStatus) {
      return `Has left: consultant contract ${d.consultantContractStatus}${d.consultantTerminationDate ? `, terminated ${formatDate(d.consultantTerminationDate)}` : ''}`;
    }
    if (d.isActive === false) {
      return `Has left: inactive in ${sourceSystemLabel(candidate?.sourceSystem)}`;
    }
    if (d.benchSource) {
      return 'Has left: recently-left talent';
    }
  }
  if (r.code === 'ALREADY_ON_BENCH' && d.startDate) {
    return `${label} since ${formatDate(d.startDate)}`;
  }
  if (r.code === 'ROLL_OFF_FAR' && d.daysUntilRollOff !== undefined) {
    return `${label} (${d.daysUntilRollOff} days, threshold ${d.threshold})`;
  }
  return label;
};

// "1 reason", "2 reasons", "1 warning", "3 warnings".
const countLabel = (count, singular, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

const reasonsTooltip = (reasons = [], candidate) => (
  <ReasonList style={{ color: 'inherit' }}>
    {reasons.map((r) => (
      <li key={r.code}>{reasonLine(r, candidate)}</li>
    ))}
  </ReasonList>
);

const importIsRunning = (candidate) => candidate.importRequests?.[0]?.status === BENCH_CANDIDATE_RUN_STATUSES.RUNNING;

// The scheduler job joins per-record findings with ' | '; render them as lines.
const importFailureTooltip = (message) => {
  const lines = String(message || '').split(' | ').map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) {
    return '';
  }
  return (
    <ReasonList style={{ color: 'inherit', paddingLeft: 16 }}>
      {lines.map((line, index) => <li key={index}>{line}</li>)}
    </ReasonList>
  );
};

/**
 * Avatar + name + detail lines, the same shape as the Bench Report row header.
 * Matched rows use the Radar employee (photo, position • grade) and link to the
 * profile; unmatched rows fall back to initials and the upstream job title.
 */
const PersonSummary = ({ candidate, onOpen }) => {
  const radar = candidate.radar;
  const avatarEmployee = radar
    ? { azureId: radar.azureId, fullName: radar.fullName || candidate.fullName }
    : { fullName: candidate.fullName };
  const positionLine = radar
    ? [radar.position?.label || candidate.jobTitle || 'No position', radar.grade?.label].filter(Boolean).join(' • ')
    : [candidate.jobTitle || 'No position', candidate.employeeType].filter(Boolean).join(' • ');
  // Delivery and direct representatives live in their own column — not repeated here.
  const contextLine = [
    candidate.country,
    candidate.location && candidate.location !== candidate.country ? candidate.location : null,
  ].filter(Boolean).join(' • ');
  const employment = employmentLineFor(candidate);

  return (
    <PersonCell>
      <EmployeeAvatar employee={avatarEmployee} size={40} />
      <PersonInfo>
        {radar?.employeeId ? (
          <div className="name name-link" onClick={() => onOpen(radar.employeeId)} role="link" tabIndex={0}>
            {candidate.fullName}
          </div>
        ) : (
          <div className="name">{candidate.fullName}</div>
        )}
        <div className="detail">{positionLine}</div>
        {contextLine && <div className="detail">{contextLine}</div>}
        {/* Consultant contract as a plain detail line — see employmentLineFor. No "Left"
            label: the Reason column and the contract text already say it; the amber tone
            is the only marker for people who have left. */}
        {employment?.text && (
          <div className={employment.left ? 'detail detail-left' : 'detail'}>{employment.text}</div>
        )}
      </PersonInfo>
    </PersonCell>
  );
};

/**
 * Work list rows. Row actions are decided by eligibility (server-computed) and
 * status; the server re-checks everything at write time.
 */
const CandidatesTable = ({
  candidates,
  permissions,
  busyId,
  onAssign,
  onUnassign,
  onCancelLeave,
  onEditBenchData,
  onDismiss,
  asOf,
  onUndismiss,
  onImport,
  onOpenEmployee,
}) => {
  const openEmployee = (employeeId) => onOpenEmployee && onOpenEmployee(employeeId);

  return (
  <TableWrapper>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Individual</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Client / SOW</TableCell>
            <TableCell>Roll-off</TableCell>
            <TableCell>Representative</TableCell>
            <TableCell>Profile</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {candidates.map((candidate) => {
            const busy = busyId === candidate.id;
            const eligible = candidate.eligibility?.eligible;
            const reasons = candidate.eligibility?.reasons || [];
            const warnings = reasons.filter((r) => !r.blocking);
            const isAssigned = candidate.status === BENCH_CANDIDATE_STATUSES.ASSIGNED;
            const isDismissed = candidate.status === BENCH_CANDIDATE_STATUSES.DISMISSED;
            const notInRadar = candidate.matchStatus !== BENCH_CANDIDATE_MATCH_STATUSES.MATCHED;
            // Recently-left / inactive people are importable too (as ex-employees
            // with closed history) — only removed-upstream rows are excluded.
            const canImport = permissions.canImport && notInRadar && !candidate.removedAt;
            const importRunning = importIsRunning(candidate);
            const lastImport = candidate.importRequests?.[0];

            return (
              <CandidateRow key={candidate.id}>
                <TableCell>
                  <PersonSummary candidate={candidate} onOpen={openEmployee} />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={BENCH_CANDIDATE_SOURCE_LABELS[candidate.benchSource] || candidate.benchSourceRaw}
                    sx={getChipSx(BENCH_CANDIDATE_SOURCE_COLORS[candidate.benchSource])}
                  />

                </TableCell>
                <TableCell>
                  <Primary>{candidate.clientName || '—'}</Primary>
                  <Secondary>SOW {candidate.sowContractSourceId || '—'}{candidate.sowStatus ? ` · ${candidate.sowStatus}` : ''}</Secondary>
                  {(candidate.sowStartDate || candidate.clientLastDate) && (
                    <Secondary>{formatDate(candidate.sowStartDate, '?')} → {formatDate(candidate.clientLastDate, '?')}</Secondary>
                  )}
                  {/* Contractually always false for a candidate — a true value contradicts the candidacy, hence the amber. */}
                  {candidate.hasFutureSowContract
                    ? <Secondary style={{ color: '#92400E' }}>has a future SOW</Secondary>
                    : <Secondary>no future SOW</Secondary>}
                </TableCell>
                <TableCell>
                  {/* rollOffStartedDate is the authoritative date (TimeTrack: last date of the
                      current contract — the day they roll off unless a new contract appears).
                      The last day at the client is the SOW period end in the Client / SOW column. */}
                  <Primary>{formatDate(candidate.rollOffStartedDate)}</Primary>
                  {/* Days are counted against the source system's calendar day (Louisville), which
                      can differ from the viewer's for a few hours — the tooltip says which day. */}
                  <Secondary title={asOf?.today ? `counted from ${formatDate(asOf.today)} (${asOf.sourceTimezone})` : undefined}>
                    {daysLabel(candidate.daysUntilRollOff)}
                  </Secondary>
                </TableCell>
                <TableCell>
                  <Primary>{candidate.deliveryManagerName || '—'}</Primary>
                  <Secondary>{candidate.directManagerName ? `direct: ${candidate.directManagerName}` : ''}</Secondary>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={BENCH_CANDIDATE_MATCH_STATUS_LABELS[candidate.matchStatus] || candidate.matchStatus}
                    sx={getChipSx(BENCH_CANDIDATE_MATCH_STATUS_COLORS[candidate.matchStatus])}
                  />
                  {/* Only when the bench allocation did NOT come from this list — for an
                      ASSIGNED row the Status column already names it with its start date. */}
                  {candidate.radar?.activeBenchEmployeeProject && !isAssigned && (
                    <Secondary>
                      on bench: {candidate.radar.activeBenchEmployeeProject.projectName}
                      {candidate.radar.activeBenchEmployeeProject.isFuture && ` · joining ${formatDate(candidate.radar.activeBenchEmployeeProject.startDate)}`}
                      {candidate.radar.activeBenchEmployeeProject.isLeaving && ` · leaving, last day ${formatDate(candidate.radar.activeBenchEmployeeProject.endDate)}`}
                    </Secondary>
                  )}
                  {importRunning && (
                    <Secondary><CircularProgress size={10} sx={{ mr: 0.5 }} />import running…</Secondary>
                  )}
                  {!importRunning && lastImport?.status === BENCH_CANDIDATE_RUN_STATUSES.FAILED && (
                    <Tooltip title={importFailureTooltip(lastImport.errorMessage)} placement="bottom-start">
                      <Secondary style={{ color: '#991B1B', cursor: 'help' }}>
                        import failed · {formatDate(lastImport.finishedAt || lastImport.requestedAt)} — hover for details
                      </Secondary>
                    </Tooltip>
                  )}
                  {!importRunning && lastImport?.status === BENCH_CANDIDATE_RUN_STATUSES.SUCCESS && notInRadar && (
                    <Secondary style={{ color: '#92400E' }}>import succeeded but no match by email — check the address in MySM</Secondary>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={BENCH_CANDIDATE_STATUS_LABELS[candidate.status] || candidate.status}
                    sx={getChipSx(BENCH_CANDIDATE_STATUS_COLORS[candidate.status])}
                  />
                  {isAssigned && candidate.removedAt && (
                    <Secondary style={{ color: '#92400E' }}>no longer listed in the {sourceSystemLabel(candidate.sourceSystem)} feed</Secondary>
                  )}
                  {isAssigned && candidate.assignedEmployeeProject && (
                    <Secondary>
                      {candidate.assignedEmployeeProject.project?.name} from {formatDate(candidate.assignedEmployeeProject.startDate)}
                      {candidate.assignedEmployeeProject.isLeaving && ` · leaving, last day ${formatDate(candidate.assignedEmployeeProject.endDate)}`}
                    </Secondary>
                  )}
                  {isDismissed && candidate.dismissReason && (
                    <Tooltip title={candidate.dismissReason}><Secondary>reason: {candidate.dismissReason.slice(0, 40)}{candidate.dismissReason.length > 40 ? '…' : ''}</Secondary></Tooltip>
                  )}
                  {!eligible && !isAssigned && reasons.length > 0 && (
                    <Tooltip title={reasonsTooltip(reasons, candidate)}>
                      <Secondary style={{ cursor: 'help' }}>not assignable · {countLabel(reasons.filter((r) => r.blocking).length, 'reason')}</Secondary>
                    </Tooltip>
                  )}
                  {eligible && warnings.length > 0 && (
                    <Tooltip title={reasonsTooltip(warnings, candidate)}>
                      <Secondary style={{ cursor: 'help', color: '#92400E' }}>{countLabel(warnings.length, 'warning')}</Secondary>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    {busy && <CircularProgress size={16} />}
                    {!busy && permissions.canUpdate && !isAssigned && eligible && (
                      <Button size="small" variant="contained" onClick={() => onAssign(candidate)}>Assign to bench</Button>
                    )}
                    {/* Mirrors the project Individuals menu: a future allocation ("Joining") is
                        cancelled, a running one ("Engaged") is ended. Server decides the same way. */}
                    {!busy && permissions.canUpdate && isAssigned && (
                      <Button size="small" variant="outlined" color="warning" onClick={() => onUnassign(candidate)}>
                        {candidate.assignedEmployeeProject?.isFuture ? 'Cancel bench allocation' : 'End bench allocation'}
                      </Button>
                    )}
                    {/* "Edit Bench Data" of the project menu, on the person's bench allocation. */}
                    {!busy && permissions.canEditBenchData && (isAssigned ? candidate.assignedEmployeeProject : candidate.radar?.activeBenchEmployeeProject) && (
                      <Button size="small" variant="text" onClick={() => onEditBenchData(candidate)}>Edit bench data</Button>
                    )}
                    {/* "Cancel Project Leave" of the project menu: a scheduled end date is removed.
                        Offered for the linked allocation, or — like the Individuals tab — for the
                        person's active bench allocation whatever created it. */}
                    {!busy && permissions.canUpdate && (isAssigned ? candidate.assignedEmployeeProject?.isLeaving : candidate.radar?.activeBenchEmployeeProject?.isLeaving) && (
                      <Button size="small" variant="text" color="warning" onClick={() => onCancelLeave(candidate)}>Cancel bench leave</Button>
                    )}
                    {!busy && canImport && !importRunning && (
                      <Button size="small" variant="outlined" onClick={() => onImport(candidate)}>Import to MySM</Button>
                    )}
                    {!busy && permissions.canUpdate && !isAssigned && !isDismissed && !candidate.removedAt && (
                      <Button size="small" variant="text" onClick={() => onDismiss(candidate)}>Dismiss</Button>
                    )}
                    {!busy && permissions.canUpdate && isDismissed && (
                      <Button size="small" variant="text" onClick={() => onUndismiss(candidate)}>Undismiss</Button>
                    )}
                  </Box>
                </TableCell>
              </CandidateRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </TableWrapper>
  );
};

export default CandidatesTable;
